import { useSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useAbility } from 'providers/casl'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { Schema } from '../../schema'
import { UPDATE_DOMAIN_UNFILLED_RECOVERY } from './api/mutation'
import { Data, Variables } from './api/types'
import { mapper } from './mapper'
import { UseUpdateDomainUnfilledRecovery } from './types'

export const useUpdateDomainUnfilledRecovery = (): UseUpdateDomainUnfilledRecovery => {
  const ability = useAbility()
  const { t } = useTranslation('features/domain')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AddEditDomainContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const { mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_DOMAIN_UNFILLED_RECOVERY,
    onCompleted: async data => {
      const {
        updateDomainUnfilledRecovery: { errors },
      } = data

      setLoading(false)

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.update'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.success.update'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['domain'],
  })

  const updateDomainUnfilledRecovery = (data: Schema): void => {
    setLoading(true)

    mutateAsync({
      input: mapper(data, ability),
    })
  }

  return { errors, updateDomainUnfilledRecovery }
}
